import {FC, HTMLAttributes} from 'react';
import {FetchStatus} from '../../../../common/FetchStatus.enum';
import useTypedSelector from '../../../../common/useTypedSelector';
import {Loading} from '../../../../design-system/atoms/Loading/Loading';
import {getSerializedPagewebError} from '../../../../errors/getSerializedPagewebError.function';
import {PagewebError} from '../../../../errors/PagewebError.class';
import {SectionType} from '../../../../graphql/generated/types';
import {useSelectSection} from '../../../../pagewebLayout/useSelectSection';
import {CommonSectionProps} from '../../type';
import Group from '../Groups';
import {Hidden} from '../Hidden';
import {BigBanner} from './BigBanner';
import {CardSection} from './CardSection';
import {DisplayAd} from './DisplayAd';
import {Episode} from './Episode';
import {EventSection} from './EventSection';
import {HelixThemeSection} from './HelixThemeSection';
import {LineBanner} from './LineBanner';
import {MainRanking} from './MainRanking';
import {PromotionBanner} from './PromotionBanner';
import {Ranking} from './Ranking';
import {RecentViewed} from './RecentViewed';
import {ScrollTheme} from './ScrollTheme';
import {StaticLandingDayOfWeek} from './StaticLandingDayOfWeek';
import {StaticLandingEvent} from './StaticLandingEvent';
import {StaticLandingFreeSeries} from './StaticLandingFreeSeries';
import {StaticLandingGenre} from './StaticLandingGenre';
import {StaticLandingOriginal} from './StaticLandingOriginal';
import {StaticLandingRanking} from './StaticLandingRanking';
import {StaticLandingRankingTheme} from './StaticLandingRankingTheme';
import {StaticLandingTodayNew} from './StaticLandingTodayNew';
import {StaticLandingTodayUp} from './StaticLandingTodayUp';
import {Theme} from './Theme';
import {Top} from './Top';

function Section({
  id,
  ...props
}: {id: string} & HTMLAttributes<HTMLDivElement>) {
  const {section, fetchStatus, error} = useSelectSection(id);
  const {groupsInSection} = useTypedSelector(state => ({
    groupsInSection: state.pagewebLayout.relations.sections[id].childrenIds,
  }));

  if (
    fetchStatus === FetchStatus.Loading ||
    fetchStatus === FetchStatus.Idle ||
    !section
  ) {
    return (
      <div className="flex-center-full">
        <Loading />
      </div>
    );
  }
  if (error) {
    throw new PagewebError(getSerializedPagewebError(error));
  }

  const ChoosedComponent =
    !section.type || !SectionTypeComponentMap[section.type]
      ? Hidden
      : SectionTypeComponentMap[section.type];

  return (
    <ChoosedComponent section={section} {...props}>
      {groupsInSection?.map(groupId => (
        <Group key={groupId} id={groupId} />
      ))}
    </ChoosedComponent>
  );
}

const SectionTypeComponentMap: {
  [key in SectionType]: FC<CommonSectionProps>;
} = {
  [SectionType.Top]: Top,
  [SectionType.LineBanner]: LineBanner,
  [SectionType.PromotionBanner]: PromotionBanner,
  [SectionType.CardBanner]: CardSection,
  [SectionType.Theme]: Theme,
  [SectionType.ScrollTheme]: ScrollTheme,
  [SectionType.Episode]: Episode,
  // 런칭 후 추가예정. 보여주지 말자
  [SectionType.Updatealarm]: Hidden,
  [SectionType.BigBanner]: BigBanner,
  [SectionType.RecentViewed]: RecentViewed,
  // 런칭 후 추가예정. 보여주지 말자
  [SectionType.DayOfWeek]: Hidden,
  [SectionType.Ranking]: Ranking,
  [SectionType.RankingV2]: Ranking,
  [SectionType.MainRanking]: MainRanking,
  // 런칭 후 추가예정. 보여주지 말자
  [SectionType.UserBasedRecommend]: Hidden,
  // 런칭 후 추가예정. 보여주지 말자
  [SectionType.SeriesBasedRecommend]: Hidden,
  // 런칭 후 추가예정. 보여주지 말자
  [SectionType.New]: Hidden,
  [SectionType.Event]: EventSection,
  [SectionType.RecommendEvent]: EventSection,
  // PC, Mobile 오픈스펙 제외
  [SectionType.CashfriendsAd]: Hidden,
  [SectionType.LandingDayOfWeek]: StaticLandingDayOfWeek,
  [SectionType.LandingTodayUp]: StaticLandingTodayUp,
  [SectionType.LandingTodayNew]: StaticLandingTodayNew,
  [SectionType.LandingRanking]: StaticLandingRanking,
  [SectionType.LandingRankingTheme]: StaticLandingRankingTheme,
  [SectionType.LandingGenre]: StaticLandingGenre,
  [SectionType.LandingEvent]: StaticLandingEvent,
  [SectionType.LandingFreeSeries]: StaticLandingFreeSeries,
  [SectionType.LandingOriginal]: StaticLandingOriginal,
  [SectionType.DisplayAd]: DisplayAd,
  [SectionType.HelixTheme]: HelixThemeSection,
  [SectionType.RankingTheme]: Ranking,
  [SectionType.Unknown]: Hidden,
};
export default Section;
